"use client";

import React, { useState, useCallback } from "react";
import Image from "next/image";
import Link from "next/link";
import dynamic from "next/dynamic";
import {
  FinanceAndAccountingSubcategory,
  FormHeading,
  SuccessMessageType,
  VerticalCategory,
} from "@/interface/types";

const GlobalForm = dynamic(() => import("@/components/forms/GlobalForm"), {
  ssr: false,
});
import { googleTagManager } from "@/components/common/utils";
import { usePathname } from "next/navigation";
import useModalScrollLock from "@/hooks/useModalScrollLock";
import useMilesOneDeepLink from "@/hooks/useMilesOneDeepLink";

interface CmaMilesoneProps {
  pageName: string;
}

const DownloadApp = () => (
  <div className="flex flex-row gap-4 justify-center">
    <div className="flex flex-col justify-center gap-3">
      <Link
        href="https://apps.apple.com/us/app/miles-us-pathway/id6504799221"
        target="_blank"
        className="bg-[#061A2F] p-2 flex rounded-lg gap-2 pr-4"
      >
        <Image
          src="https://ik.imagekit.io/mileseducation/miles_website/home/app-store.webp?tr=w-48,h-49,q-80"
          alt="App Store"
          width={48}
          height={49}
          className="shrink-0"
          loading="lazy"
          sizes="48px"
        />
        <div>
          <p className="text-[10px] leading-3 text-white font-medium">
            Download on the
          </p>
          <p className="text-base text-white font-medium">App Store</p>
        </div>
      </Link>
      <Link
        href="https://play.google.com/store/apps/details?id=com.miles.one"
        target="_blank"
        className="bg-[#061A2F] p-2 flex rounded-lg gap-2 pr-4"
      >
        <Image
          src="https://ik.imagekit.io/mileseducation/miles_website/home/play-store.webp?tr=w-48,h-49,q-80"
          alt="Play Store"
          width={48}
          height={49}
          className="shrink-0"
          loading="lazy"
          sizes="48px"
        />
        <div>
          <p className="text-[10px] leading-3 text-white font-medium">
            GET IT ON
          </p>
          <p className="text-base text-white font-medium">Google Play</p>
        </div>
      </Link>
    </div>
    <div className="text-center">
      <Image
        src="https://ik.imagekit.io/mileseducation/miles_website/home/milesone_qr_code.webp"
        alt="QR Code"
        width={100}
        height={100}
        className="min-w-[100px] min-h-[100px] inline-block"
        loading="lazy"
        sizes="100px"
      />
      <p className="pt-1 text-xs text-[#fff]">Scan to download</p>
    </div>
  </div>
);

export default function CmaMilesone({ pageName }: CmaMilesoneProps) {
  const pathname = usePathname();
  const [isFormModalOpen, setIsFormModalOpen] = useState<boolean>(false);

  // Deep link hook for mobile app redirect
  const { tryOpenApp, isAndroid, isIOS } = useMilesOneDeepLink();

  // Check if current page is the special fees page
  const isFeesPage =
    pathname === "/best-us-cpa-admission-online-course-fees-in-india";

  // Pages that require form submission for lead capturing
  const formRequiredPages = [
    "/best-us-cpa-admission-online-course-fees-in-india",
    "/best-us-cma-coaching-in-india",
    "/miles-education-cpa-fees-miles-education",
    "/miles-us-cpa-miles-us-cma",
  ];

  const shouldShowForm = formRequiredPages.includes(pathname);

  // GTM event mapping for Download Now button
  const downloadNowGtmEventMap: Record<string, string> = {
    "/miles-us-cpa-miles-us-cma": "Brand_LP_DownloadNow",
    "/best-us-cpa-admission-online-course-fees-in-india": "CPA_LP_DownloadNow",
    "/best-us-cma-coaching-in-india": "CMA_LP_DownloadNow",
  };

  const handleDownloadNowClick = useCallback(() => {
    const eventName = downloadNowGtmEventMap[pathname];
    if (eventName) {
      googleTagManager(eventName, {
        button_location: "cma_milesone_section",
        page_url: typeof window !== "undefined" ? window.location.href : "",
      });
    }

    const isMobile = isAndroid || isIOS;

    if (shouldShowForm) {
      // Lead capturing pages: Show form modal for both mobile and desktop
      sessionStorage.setItem("modal-open", "true");
      setIsFormModalOpen(true);
    } else if (isMobile) {
      // Other pages on mobile: Redirect directly to app store
      tryOpenApp();
    } else {
      // Other pages on desktop: Show simple modal
      sessionStorage.setItem("modal-open", "true");
      setIsFormModalOpen(true);
    }
  }, [
    pathname,
    isAndroid,
    isIOS,
    shouldShowForm,
    tryOpenApp,
    downloadNowGtmEventMap,
  ]);

  const handleFormModelClose = useCallback(() => {
    sessionStorage.removeItem("modal-open");
    setIsFormModalOpen(false);
  }, []);

  useModalScrollLock(isFormModalOpen, {
    restoreScrollPosition: true,
    preventTouchScroll: true,
  });

  return (
    <>
      <div className="w-full bg-center relative isolate md:py-8 py-10">
        {/* DESKTOP BG */}
        <div
          className="hidden md:block absolute inset-0 -z-10 pointer-events-none select-none"
          style={{ contain: "layout style paint" }}
        >
          <Image
            src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa/mileone-web-desk.webp"
            alt="CMA Visit Hero Section Background"
            fill
            className="object-cover"
            sizes="(min-width: 769px) 100vw, 0px"
            loading="lazy"
            quality={100}
          />
        </div>

        {/* MOBILE BG */}
        <div
          className="md:hidden absolute inset-0 -z-10 pointer-events-none select-none"
          style={{ contain: "layout style paint" }}
        >
          <Image
            src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa/mileone-web-mob.webp?tr=w-480,h-310,q-80"
            alt="CMA Visit Hero Section Bg Mob"
            fill
            className=""
            sizes="(max-width: 767px) 480px, 0px"
            loading="lazy"
            quality={80}
          />
        </div>

        {/* CONTENT */}
        <div className="max-w-[1340px] mx-auto relative px-4 md:px-6 lg:px-7 py-6 lg:py-16">
          <div className="flex flex-col md:items-start items-start md:text-left text-left md:space-y-4">
            <p className="text-base md:text-2xl text-white">
              {isFeesPage ? "Get the" : "Download the"}
            </p>

            <h2 className="text-3xl md:text-[50px] font-bold text-white tracking-tight">
              Miles One App
            </h2>

            <p className="text-base md:text-2xl text-white font-semibold">
              FREE trial for CMA + CAIRA
            </p>
          </div>
          {/* Conditional rendering based on page */}
          {isFeesPage ? (
            // Show App Store and Play Store buttons for fees page
            <div className="flex flex-row gap-3 justify-start md:justify-center md:items-center md:mt-10 mt-4">
              <div
                className="bg-[#3D3D3D] p-2 flex rounded-lg w-[100px] md:w-auto gap-2 pr-4 md:pr-6"
                onClick={(e) => e.preventDefault()} // Disable click for fees page
              >
                <Image
                  src="https://ik.imagekit.io/mileseducation/miles_website/home/app-store.webp"
                  alt="App Store"
                  width={48}
                  height={49}
                  loading="lazy"
                  className="shrink-0 w-6 h-6 md:w-[32px] md:h-[32px]"
                  sizes="(max-width: 767px) 24px, 32px"
                />
                <div>
                  <p className="text-[8px] md:text-[10px] leading-3 text-white font-medium">
                    Download on
                  </p>
                  <p className="text-[10px] md:text-base text-white font-medium">
                    App Store
                  </p>
                </div>
              </div>
              <div
                className="bg-[#3D3D3D] p-2 flex rounded-lg w-[100px] md:w-auto gap-2 pr-4 md:pr-6"
                onClick={(e) => e.preventDefault()} // Disable click for fees page
              >
                <Image
                  src="https://ik.imagekit.io/mileseducation/miles_website/home/play-store.webp?tr=w-48,h-49,q-80"
                  alt="Play Store"
                  width={48}
                  height={49}
                  loading="lazy"
                  className="shrink-0 w-6 h-6 md:w-[32px] md:h-[32px]"
                  sizes="(max-width: 767px) 24px, 32px"
                />
                <div>
                  <p className="text-[8px] md:text-[10px] leading-3 text-white font-medium">
                    Download on
                  </p>
                  <p className="text-[10px] md:text-base text-white font-medium">
                    Play Store
                  </p>
                </div>
              </div>
            </div>
          ) : (
            // Show Download Now button for other pages
            <div className="flex md:justify-start justify-start gap-4 md:mt-10 mt-4">
              <button
                onClick={handleDownloadNowClick}
                className="md:px-2 md:py-2 py-2 px-1 cursor-pointer bg-[#fff] text-[#000] font-semibold text-sm md:w-44 w-[124px] md:text-base transition-colors text-center duration-300 hover:bg-gray-100 rounded-full"
              >
                Download Now
              </button>
            </div>
          )}
        </div>
      </div>
      {isFormModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-center z-[999] lg:p-10 p-4">
          <div
            className="rounded-2xl shadow-lg relative h-[auto] mx-auto border border-white p-4 w-[400px]"
            style={{
              background:
                "linear-gradient(180deg, rgba(255, 255, 255, 0.30) 0%, rgba(51, 153, 255, 0.30) 100%)",
              boxShadow: "0px 0px 14px 0px rgba(4, 84, 175, 0.29)",
              backdropFilter: "blur(28px)",
            }}
          >
            <button
              onClick={handleFormModelClose}
              className="text-white text-xl md:text-2xl absolute right-1 top-1 cursor-pointer"
            >
              ✕
            </button>

            {shouldShowForm ? (
              // Show form for lead capturing pages
              <div>
                <Image
                  src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit/starGroup.webp"
                  alt="star group"
                  width={34}
                  height={38}
                  className="absolute top-3 left-[-8px]"
                  loading="lazy"
                  sizes="34px"
                />
                <GlobalForm
                  course={pageName}
                  courseId={
                    pageName === "CPA"
                      ? 1
                      : pageName === "CMA"
                        ? 2
                        : pageName === "EA"
                          ? 19
                          : 1
                  }
                  formHeading={
                    FormHeading.SCHEDULE_A_VISIT_TO_MILES_OFFICE_OR_MEET_US_ONLINE
                  }
                  formType="page"
                  pageType="accounting page"
                  subCategory={
                    pageName === "CPA"
                      ? FinanceAndAccountingSubcategory.CPA
                      : pageName === "CMA"
                        ? FinanceAndAccountingSubcategory.CMA
                        : FinanceAndAccountingSubcategory.EA
                  }
                  submitButtonName="Download App"
                  successMessage={SuccessMessageType.ACCOUNTING}
                  verticalName={VerticalCategory.FINANCE_AND_ACCOUNTING}
                  darkBackground={true}
                  isMilesSection
                />
              </div>
            ) : (
              // Show simple modal for other pages
              <div className="text-center pt-4">
                <h3 className="text-white text-2xl font-semibold mb-2">
                  Get Miles One App
                </h3>
                <p className="text-[#fff] text-sm mb-4">
                  The #1 App for AI-Ready Accountants
                </p>

                <div className="md:block hidden">
                  <DownloadApp />
                </div>

                {/* Download MilesOne App Card */}
                <div className="mt-4 bg-[linear-gradient(94deg,_#061A2F_0.32%,_#135295_99.92%)] rounded-xl p-3 md:hidden block">
                  <div className="flex gap-3 items-center">
                    <Image
                      src="/miles_logo_for_form.png"
                      alt="Miles Logo"
                      width={24}
                      height={14}
                      className="cursor-pointer h-4"
                      loading="lazy"
                      sizes="24px"
                    />
                    <p className="text-base font-semibold text-white">
                      {isFeesPage
                        ? "Get the MilesOne App now!"
                        : "Download the MilesOne App now!"}
                    </p>
                  </div>
                  <button
                    className="w-full px-1 py-2 justify-center rounded-[12px_12px_12px_0px] mt-3 flex bg-white text-[#316BC9] font-semibold"
                    onClick={() => {
                      tryOpenApp();
                    }}
                  >
                    Open
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
